import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  useTypeDisplayedSpinner,
  useShowLessFiler,
  useBuildType,
  useHideFilterZone,
} from 'src/hooks';
import { requestSelectors } from 'src/store/requests/selectors';
import { TypeViewIconName, listOfTypes, IListOfTypes } from 'src/store/type-displayed-spinner/constant';
import { pathList } from 'src/route-list';
import { TypesOfSpinner } from 'src/typings/types-of-spinner';
import { getCurrentLang } from 'src/store/app/selectors';
import { useSetSection } from 'src/hooks/floors';
import { TypeDisplayedSpinnerProps } from 'src/components/FilterSpinner/components/TypeDisplayedSpinner/types';
import Box from '@mui/material/Box';
import {
  AroundImage,
  LayoutsImage,
  FloorsImage,
  CrosstableIcon,
} from './components';
import { MasterPlanDropdown } from './components/MasterPlanDropdown';
import { useStyles as useStylesSpinner } from './styles-spinner';
import { useStyles as useStylesCrossTable } from './styles-crosstable';

interface ITypeDisplayedSpinner {
  isActive?: boolean;
  pathChanged?: (path: string) => void;
}

export const TypeDisplayedSpinner: React.FC<ITypeDisplayedSpinner> = React.memo(({
  isActive,
  pathChanged,
}) => {
  const history = useHistory();
  const currentLang = useSelector(getCurrentLang);
  const {
    apartmentFloor, apartmentSection, apartmentHouse, apartmentNumeration,
  }: TypeDisplayedSpinnerProps = useParams();
  const { isSpinner, isCrossTable } = useBuildType();
  const mainColor = useSelector(requestSelectors.app.getMainColors);
  const styles = isSpinner ? useStylesSpinner(mainColor) : useStylesCrossTable();
  const {
    selectedViews,
    getSelectedViewId,
    handleOnChangeTypeMap,
    handleDropToPreviousView,
  } = useTypeDisplayedSpinner();
  const { handleToggleShowAllFilter } = useShowLessFiler();
  useHideFilterZone();
  const { setSection } = useSetSection();

  useEffect(() => {
    if (apartmentFloor && apartmentSection) {
      handleOnChangeTypeMap(TypesOfSpinner.viewFloors);
      setSection('section_id', apartmentSection);
    }
  }, [apartmentFloor, apartmentSection, apartmentHouse, apartmentNumeration]);

  const handleMasterPlan = useCallback((type: IListOfTypes) => {
    handleOnChangeTypeMap(type.id);
    handleDropToPreviousView();
    let path = type.path || '/';
    if (!isActive) {
      path = pathList.home;
    }
    if (!isCrossTable) {
      history.push(path);
    }
    pathChanged?.(path);
  }, [isActive, history, handleOnChangeTypeMap, handleDropToPreviousView]);

  const handleTypeBtn = useCallback((type: IListOfTypes) => {
    handleOnChangeTypeMap(type.id);
    if (type.showNestedFilter) {
      handleToggleShowAllFilter(true);
    }
    if (type.isMain) {
      handleDropToPreviousView();
    }
    let path = type.path || '/';
    if (!isActive) {
      path = pathList.home;
    }
    if (!isCrossTable) {
      history.push(path);
    }
    pathChanged?.(path);
  }, [history, isActive, handleOnChangeTypeMap, handleToggleShowAllFilter, handleDropToPreviousView]);

  const renderIcon = (iconName: TypeViewIconName) => {
    switch (iconName) {
      case TypeViewIconName.svg360:
        return <AroundImage />;
      case TypeViewIconName.layouts:
        return <LayoutsImage />;
      case TypeViewIconName.floors:
        return <FloorsImage />;
      case TypeViewIconName.model2d:
        return <CrosstableIcon />;
      default:
        return null;
    }
  };

  return (
    <>
      {
        selectedViews.map((view) => {
          const type: IListOfTypes = listOfTypes[view.type];
          if (view.type === TypesOfSpinner.viewSpinner) {
            return (
              <MasterPlanDropdown
                key={type.id}
                getSelectedViewId={getSelectedViewId}
                view={view}
                isActive={!!isActive}
                handleClick={() => handleMasterPlan(type)}
              />
            );
          }

          return (
            <Box
              key={type.id}
              className={`${styles.wrapperTypeItem} ${
                type.id === getSelectedViewId && isActive ? styles.selectedStyle : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleTypeBtn(type)}
            >
              <div className={styles.iconTypeStyles}>
                { renderIcon(type.iconName) }
              </div>
              <span>{ view.name.translates?.[currentLang] || view.name.systemName }</span>
            </Box>
          );
        })
      }
    </>
  );
});
